import { compact } from "lodash";
import { PropsWithChildren, useMemo } from "react";
import {
  Redirect,
  Route,
  Switch,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { AssessmentType } from "types/graphql-schema";

import Assessment from "@apps/assessments/components/assessment/assessment";
import StartReview from "@apps/assessments/components/assessment/start-review";
import ProfileResponsiveTabs from "@apps/profile/profile-responsive-tabs";
import ComplianceProgram from "@apps/programs/components/compliance-program";
import ComplianceProgramForm from "@apps/programs/components/compliance-program-form/compliance-program-form";
import { ComplianceProgramType } from "@apps/programs/components/compliance-program-form/types";
import ProgramProgressPage from "@apps/programs/components/program-progress-page";
import {
  ProgramsSection,
  ProgramsSectionContext,
} from "@apps/programs/programs-section-context";
import AssessmentReportPage from "@apps/reporting/components/assessment-report-page";
import TrendReportPage from "@apps/reporting/components/trend-report-page";
import useLabel from "@apps/use-label/use-label";
import { isAdminVar, isManagerVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Layout from "@components/layout/layout";
import { useLink } from "@components/link/link-helpers";
import { ToggleButtonGroupType } from "@components/toggle-button-group/toggle-button-group";
import useDocumentTitle from "@components/use-document-title/use-document-title";

import AssessmentDeliveryPage from "./components/delivery/assessment-delivery-page";
import AssessmentsHomepage from "./components/homepage";

const REVIEW_TAB_ROUTES: Record<string, string> = {
  active: "/assessments/active",
  all: "/assessments/all",
  delivered: "/assessments/delivered",
  progress: "/assessments/progress",
  results: "/assessments/results",
  trends: "/assessments/trends",
};

const AssessmentsNavContainer = (
  props: PropsWithChildren & { showNav?: boolean },
) => {
  const label = useLabel();
  const isAdmin = isAdminVar();
  const isManager = isManagerVar();
  const canManage = isAdmin || isManager;
  const link = useLink();
  const { pathname } = useLocation();

  const activeTab =
    pathname === "/assessments/all"
      ? "all"
      : pathname === "/assessments/delivered"
        ? "delivered"
        : pathname === "/assessments/progress"
          ? "progress"
          : pathname === "/assessments/results"
            ? "results"
            : pathname === "/assessments/trends"
              ? "trends"
              : "active";

  const tabButtons: ToggleButtonGroupType<string>[] = compact([
    { value: "active", title: "Active", active: activeTab === "active" },
    {
      value: "delivered",
      title: "Delivered",
      active: activeTab === "delivered",
    },
    canManage && { value: "all", title: "All", active: activeTab === "all" },
    canManage && {
      value: "progress",
      title: "Progress",
      active: activeTab === "progress",
    },
    canManage && {
      value: "results",
      title: "Results",
      active: activeTab === "results",
    },
    canManage && {
      value: "trends",
      title: "Trends",
      active: activeTab === "trends",
    },
  ]);

  const handleTabClick = (button: ToggleButtonGroupType<string>) => {
    link.redirect(
      REVIEW_TAB_ROUTES[button.value ?? "active"] ?? "/assessments",
    );
  };

  return (
    <Layout>
      <Layout.Header
        breadcrumbs={[
          {
            title: label("review", { capitalize: true, pluralize: true }),
            url: "/assessments",
          },
        ]}
        afterTitle={
          <ProfileResponsiveTabs
            buttons={tabButtons}
            onClick={handleTabClick}
            dividerAfterIndex={2}
          />
        }
      >
        {(isAdmin || isManager) && (
          <div className="flex justify-end gap-4 items-center">
            <Button
              text={`Create ${label("review", { capitalize: true })}`}
              to="/assessments/program/new"
              theme={buttonTheme.primary}
              size={ButtonSize.medium}
            />
          </div>
        )}
      </Layout.Header>
      <Layout.Container>
        {props.showNav ? (
          <Layout.Main width="full">
            <Layout.MainSection>{props.children}</Layout.MainSection>
          </Layout.Main>
        ) : (
          props.children
        )}
      </Layout.Container>
    </Layout>
  );
};

// Wraps an embedded Reporting/Trends view with the same scroll container the
// standalone Reporting page uses, so it fills the tab body cleanly. When a
// `header` is given, it renders above the report in normal flow (decoupled from
// the report's own absolute-positioned scroll body).
const ReportTabContainer = ({
  children,
  header,
  innerClassName = "",
}: PropsWithChildren & { header?: string; innerClassName?: string }) =>
  header ? (
    <main className="absolute inset-0 flex flex-col overflow-hidden">
      <div className="@container/layout-main mx-auto flex w-full flex-1 flex-col min-h-0">
        <div className="px-8 pt-6 pb-2">
          <div className="text-lg font-semibold text-gray-900">{header}</div>
        </div>
        <div className="relative flex-1 min-h-0">{children}</div>
      </div>
    </main>
  ) : (
    <main className="absolute inset-0 overflow-auto">
      <div className={`@container/layout-main mx-auto ${innerClassName}`}>
        {children}
      </div>
    </main>
  );

const Assessments = () => {
  const label = useLabel();
  useDocumentTitle(label("review", { capitalize: true, pluralize: true }));
  const { path } = useRouteMatch();
  const isAdmin = isAdminVar();
  const isManager = isManagerVar();
  const canManage = isAdmin || isManager;

  const reviewLabelPlural = label("review", {
    capitalize: true,
    pluralize: true,
  });
  const programsSection: ProgramsSection = useMemo(
    () => ({
      basePath: "/assessments/program",
      listUrl: "/assessments/all",
      rootCrumb: { title: reviewLabelPlural, url: "/assessments" },
      lockedProgramType: ComplianceProgramType.REVIEW,
      assessmentTypes: [
        AssessmentType.Performance,
        AssessmentType.Manager,
        AssessmentType.Peer,
      ],
      noun: label("review", { capitalize: true }),
      nounPlural: reviewLabelPlural,
      listEmptyText: `No ${label("review", { pluralize: true })}.`,
    }),
    [reviewLabelPlural, label],
  );

  return (
    <ProgramsSectionContext.Provider value={programsSection}>
      <Switch>
        <Route
          exact
          path={[
            `${path}/home`,
            `${path}/active`,
            `${path}/delivered`,
            `${path}`,
          ]}
        >
          <AssessmentsNavContainer>
            <AssessmentsHomepage />
          </AssessmentsNavContainer>
        </Route>

        {canManage && (
          <Route exact path={`${path}/all`}>
            <AssessmentsNavContainer>
              <AssessmentsHomepage />
            </AssessmentsNavContainer>
          </Route>
        )}

        {canManage && (
          <Route exact path={`${path}/progress`}>
            <AssessmentsNavContainer>
              <ProgramProgressPage />
            </AssessmentsNavContainer>
          </Route>
        )}

        {canManage && (
          <Route exact path={`${path}/results`}>
            <AssessmentsNavContainer>
              <ReportTabContainer header="Results">
                <AssessmentReportPage
                  assessmentType={AssessmentType.Performance}
                  embedded
                  enableUpwardToggle
                />
              </ReportTabContainer>
            </AssessmentsNavContainer>
          </Route>
        )}

        {canManage && (
          <Route exact path={`${path}/trends`}>
            <AssessmentsNavContainer>
              <ReportTabContainer innerClassName="pt-6">
                <TrendReportPage />
              </ReportTabContainer>
            </AssessmentsNavContainer>
          </Route>
        )}

        <Route
          exact
          path={[
            `${path}/program/:complianceProgramId(\\d+)`,
            `${path}/program/:complianceProgramId(\\d+)/actions`,
            `${path}/program/:complianceProgramId(\\d+)/reminders`,
            `${path}/program/:complianceProgramId(\\d+)/activity`,
            `${path}/program/:complianceProgramId(\\d+)/timeline`,
            `${path}/program/:complianceProgramId(\\d+)/progress`,
            `${path}/program/:complianceProgramId(\\d+)/reporting/:reportTab?`,
          ]}
        >
          <ComplianceProgram />
        </Route>

        {(isAdmin || isManager) && (
          <Route
            exact
            path={[
              `${path}/program/new`,
              `${path}/program/:complianceProgramId(\\d+)/edit`,
              `${path}/program/:complianceProgramId(\\d+)/duplicate`,
            ]}
          >
            <ComplianceProgramForm />
          </Route>
        )}

        <Route
          exact
          path={`${path}/start/:complianceProgramId(\\d+)/:templateId(\\d+)/:targetId(\\d+)`}
        >
          <StartReview />
        </Route>
        <Route exact path={`${path}/assessment/:assessmentId(\\d+)`}>
          <Assessment />
        </Route>
        <Route
          exact
          path={`${path}/assessment/delivery/:assessmentDeliveryId(\\d+)`}
        >
          <AssessmentDeliveryPage />
        </Route>

        {/* Non-managers hitting a management-only tab URL land on Active. */}
        <Route>
          <Redirect to={`${path}/active`} />
        </Route>
      </Switch>
    </ProgramsSectionContext.Provider>
  );
};

export default Assessments;
